import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Link as LinkIcon, Plus, X, Pencil, Trash,
  Briefcase, Cross, CurrencyDollar, Brain, Wrench, BookOpen, 
  Microphone, DotsThreeCircle, Star, Crown, ArrowSquareOut,
  MagnifyingGlass, FunnelSimple, CaretDown, Image as ImageIcon
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  business: Briefcase,
  faith: Cross,
  finance: CurrencyDollar,
  mindset: Brain,
  tools: Wrench,
  books: BookOpen,
  podcasts: Microphone,
  other: DotsThreeCircle
};

// Category colors
const CATEGORY_COLORS = {
  business: '#10B981',
  faith: '#8B5CF6',
  finance: '#F59E0B',
  mindset: '#EC4899',
  tools: '#3B82F6',
  books: '#EF4444',
  podcasts: '#06B6D4',
  other: '#6B7280'
};

const LinksResourcesPage = () => {
  const { user, isAdmin, isPaidMember, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    image_url: '',
    category: 'other',
    is_featured: false,
    is_premium: false,
    order: 0
  });

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/api/resources/links/categories`);
        setCategories(response.data);
      } catch (error) {
      }
    };
    fetchCategories();
  }, []);

  // Fetch links
  useEffect(() => {
    const fetchLinks = async () => {
      try {
        let url = `${API}/api/resources/links`;
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') {
          params.append('category', selectedCategory);
        }
        if (showFeaturedOnly) {
          params.append('featured_only', 'true');
        }
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        
        const response = await axios.get(url, { headers: getAuthHeader() });
        setLinks(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchLinks();
  }, [selectedCategory, showFeaturedOnly, getAuthHeader]);

  // Filter links by search
  const filteredLinks = links.filter(link => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      link.title.toLowerCase().includes(query) ||
      link.description.toLowerCase().includes(query)
    );
  });

  // Featured links
  const featuredLinks = filteredLinks.filter(link => link.is_featured);
  const regularLinks = filteredLinks.filter(link => !link.is_featured);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      image_url: '',
      category: 'other',
      is_featured: false,
      is_premium: false,
      order: 0
    });
    setEditingLink(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleOpenEdit = (link) => {
    setFormData({
      title: link.title,
      description: link.description,
      url: link.url,
      image_url: link.image_url || '',
      category: link.category,
      is_featured: link.is_featured,
      is_premium: link.is_premium,
      order: link.order
    });
    setEditingLink(link);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingLink) {
        await axios.put(
          `${API}/api/resources/links/${editingLink.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Resource updated');
      } else {
        await axios.post(
          `${API}/api/resources/links`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Resource added');
      }
      
      // Refresh links
      const response = await axios.get(`${API}/api/resources/links`, { headers: getAuthHeader() });
      setLinks(response.data);
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save resource');
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      await axios.delete(`${API}/api/resources/links/${linkId}`, { headers: getAuthHeader() });
      toast.success('Resource deleted');
      setLinks(links.filter(l => l.id !== linkId));
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-[#1a1a1a] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-black text-[#F7F7F5]">Links & Resources</h1>
                <p className="text-sm text-[#A3A3A3]">Curated resources for your journey</p>
              </div>
            </div>
            
            {isAdmin && (
              <Button 
                onClick={handleOpenAdd}
                className="btn-gold"
                data-testid="add-resource-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Resource
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&auto=format&fit=crop&q=80"
            alt="Resources"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#C4A661]/10 via-transparent to-[#8B5CF6]/10" />
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#C4A661]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-[#8B5CF6]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C4A661]/10 border border-[#C4A661]/30 rounded-full mb-6">
              <LinkIcon size={16} className="text-[#C4A661]" />
              <span className="text-sm font-medium text-[#C4A661]">Valuable Resources</span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-black text-[#F7F7F5] mb-6 leading-tight">
              Tools for <span className="text-[#C4A661]">Kingdom Builders</span>
            </h2>
            <p className="text-lg md:text-xl text-[#A3A3A3] mb-10 max-w-2xl mx-auto">
              Handpicked resources to help you grow in faith, business, and personal development. Everything you need to build your legacy.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full bg-[#141414] border border-[#262626] rounded-xl pl-12 pr-4 py-4 text-[#F7F7F5] placeholder-[#525252] focus:outline-none focus:border-[#C4A661] transition-colors"
                data-testid="search-input"
              />
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center justify-center gap-8 mt-10">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#C4A661]">{links.length}</p>
                <p className="text-xs text-[#525252] uppercase tracking-wider">Resources</p>
              </div>
              <div className="w-px h-8 bg-[#262626]" />
              <div className="text-center">
                <p className="text-2xl font-bold text-[#C4A661]">{categories.length}</p>
                <p className="text-xs text-[#525252] uppercase tracking-wider">Categories</p>
              </div>
              <div className="w-px h-8 bg-[#262626]" />
              <div className="text-center">
                <p className="text-2xl font-bold text-[#C4A661]">{featuredLinks.length}</p>
                <p className="text-xs text-[#525252] uppercase tracking-wider">Featured</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#1a1a1a] bg-[#0A0A0A] sticky top-[73px] z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Category Filter */}
            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-[#141414] border border-[#262626] rounded-lg text-[#F7F7F5] hover:border-[#C4A661] transition-colors"
                data-testid="category-filter"
              >
                <FunnelSimple size={18} />
                <span>{selectedCategory === 'all' ? 'All Categories' : getCategoryLabel(selectedCategory)}</span>
                <CaretDown size={14} />
              </button>
              
              <AnimatePresence>
                {showCategoryDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-[#141414] border border-[#262626] rounded-xl shadow-xl z-50 overflow-hidden"
                  >
                    <button
                      onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                      className={`w-full px-4 py-3 text-left hover:bg-[#262626] transition-colors ${selectedCategory === 'all' ? 'text-[#C4A661]' : 'text-[#F7F7F5]'}`}
                    >
                      All Categories
                    </button>
                    {categories.map(cat => {
                      const Icon = CATEGORY_ICONS[cat.value] || DotsThreeCircle;
                      return (
                        <button
                          key={cat.value}
                          onClick={() => { setSelectedCategory(cat.value); setShowCategoryDropdown(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#262626] transition-colors ${selectedCategory === cat.value ? 'text-[#C4A661]' : 'text-[#F7F7F5]'}`}
                        >
                          <Icon size={18} style={{ color: CATEGORY_COLORS[cat.value] }} />
                          <span>{cat.label}</span>
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Featured Toggle */}
            <button
              onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFeaturedOnly 
                  ? 'bg-[#C4A661] text-[#0A0A0A]' 
                  : 'bg-[#141414] border border-[#262626] text-[#F7F7F5] hover:border-[#C4A661]'
              }`}
            >
              <Star size={18} weight={showFeaturedOnly ? 'fill' : 'regular'} />
              <span>Featured</span>
            </button>
            
            {/* Results count */}
            <span className="text-sm text-[#525252] ml-auto">
              {filteredLinks.length} resource{filteredLinks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-[#C4A661] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#A3A3A3]">Loading resources...</p>
          </div>
        ) : filteredLinks.length === 0 ? (
          <div className="text-center py-20">
            <LinkIcon size={48} className="text-[#525252] mx-auto mb-4" />
            <p className="text-[#A3A3A3] mb-2">No resources found</p>
            <p className="text-sm text-[#525252]">
              {searchQuery ? 'Try a different search term' : 'Resources will appear here once added'}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Featured Section */}
            {featuredLinks.length > 0 && !showFeaturedOnly && (
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <Star size={20} className="text-[#C4A661]" weight="fill" />
                  <h3 className="text-lg font-bold text-[#F7F7F5]">Featured Resources</h3>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredLinks.map((link, idx) => (
                    <ResourceCard 
                      key={link.id} 
                      link={link} 
                      index={idx}
                      isAdmin={isAdmin}
                      onEdit={() => handleOpenEdit(link)}
                      onDelete={() => handleDelete(link.id)}
                    />
                  ))}
                </div>
              </section>
            )}
            
            {/* All Resources */}
            <section>
              {!showFeaturedOnly && featuredLinks.length > 0 && (
                <h3 className="text-lg font-bold text-[#F7F7F5] mb-6">All Resources</h3>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(showFeaturedOnly ? featuredLinks : regularLinks).map((link, idx) => (
                  <ResourceCard 
                    key={link.id} 
                    link={link} 
                    index={idx}
                    isAdmin={isAdmin}
                    onEdit={() => handleOpenEdit(link)}
                    onDelete={() => handleDelete(link.id)}
                  />
                ))}
              </div>
            </section>
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#141414] border border-[#262626] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#F7F7F5]">
                  {editingLink ? 'Edit Resource' : 'Add Resource'}
                </h3>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="text-[#A3A3A3] hover:text-[#F7F7F5]"
                >
                  <X size={24} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#A3A3A3] mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Resource title"
                    required
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] placeholder-[#525252] focus:outline-none focus:border-[#C4A661]"
                    data-testid="resource-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#A3A3A3] mb-2">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this resource"
                    required
                    rows={3}
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] placeholder-[#525252] focus:outline-none focus:border-[#C4A661] resize-none"
                    data-testid="resource-description-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#A3A3A3] mb-2">URL *</label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://example.com"
                    required
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] placeholder-[#525252] focus:outline-none focus:border-[#C4A661]"
                    data-testid="resource-url-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#A3A3A3] mb-2">
                    <span className="flex items-center gap-2">
                      <ImageIcon size={16} />
                      Header Image URL
                    </span>
                  </label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] placeholder-[#525252] focus:outline-none focus:border-[#C4A661]"
                    data-testid="resource-image-input"
                  />
                  {formData.image_url && (
                    <div className="mt-2 rounded-lg overflow-hidden h-32 bg-[#0A0A0A]">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#A3A3A3] mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] focus:outline-none focus:border-[#C4A661]"
                    data-testid="resource-category-select"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="w-5 h-5 rounded border-[#262626] bg-[#0A0A0A] text-[#C4A661] focus:ring-[#C4A661]"
                    />
                    <span className="text-sm text-[#F7F7F5] flex items-center gap-1">
                      <Star size={16} className="text-[#C4A661]" />
                      Featured
                    </span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_premium}
                      onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
                      className="w-5 h-5 rounded border-[#262626] bg-[#0A0A0A] text-[#C4A661] focus:ring-[#C4A661]"
                    />
                    <span className="text-sm text-[#F7F7F5] flex items-center gap-1">
                      <Crown size={16} className="text-[#C4A661]" />
                      Inner Circle Only
                    </span>
                  </label>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    className="btn-gold"
                    data-testid="submit-resource-btn"
                  >
                    {editingLink ? 'Update' : 'Add'} Resource
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Resource Card Component
const ResourceCard = ({ link, index, isAdmin, onEdit, onDelete }) => {
  const Icon = CATEGORY_ICONS[link.category] || DotsThreeCircle;
  const accentColor = CATEGORY_COLORS[link.category] || '#6B7280';
  
  return (
    <motion.a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group block bg-[#141414] border border-[#262626] rounded-xl overflow-hidden hover:border-[#C4A661]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#C4A661]/5"
      data-testid={`resource-card-${link.id}`}
    >
      {/* Image Header */}
      <div className="relative h-40 bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] overflow-hidden">
        {link.image_url ? (
          <img 
            src={link.image_url} 
            alt={link.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={48} style={{ color: accentColor }} weight="duotone" className="opacity-30" />
          </div>
        )}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {link.is_featured && (
            <span className="px-2 py-1 bg-[#C4A661] text-[#0A0A0A] text-xs font-bold rounded flex items-center gap-1">
              <Star size={12} weight="fill" />
              Featured
            </span>
          )}
          {link.is_premium && (
            <span className="px-2 py-1 bg-[#8B5CF6] text-white text-xs font-bold rounded flex items-center gap-1">
              <Crown size={12} weight="fill" />
              Premium
            </span>
          )}
        </div>
        
        {/* Admin Actions */}
        {isAdmin && (
          <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.preventDefault(); onEdit(); }}
              className="w-8 h-8 bg-black/60 backdrop-blur rounded-lg flex items-center justify-center text-white hover:bg-[#C4A661] hover:text-[#0A0A0A] transition-colors"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); onDelete(); }}
              className="w-8 h-8 bg-black/60 backdrop-blur rounded-lg flex items-center justify-center text-white hover:bg-red-500 transition-colors"
            >
              <Trash size={14} />
            </button>
          </div>
        )}
        
        {/* Category Badge */}
        <div 
          className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5"
          style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
        >
          <Icon size={12} weight="fill" />
          <span className="capitalize">{link.category}</span>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h4 className="text-lg font-bold text-[#F7F7F5] mb-2 group-hover:text-[#C4A661] transition-colors line-clamp-1">
          {link.title}
        </h4>
        <p className="text-sm text-[#A3A3A3] line-clamp-2 mb-4">
          {link.description}
        </p>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#525252] truncate max-w-[70%]">
            {new URL(link.url).hostname.replace('www.', '')}
          </span>
          <span className="flex items-center gap-1 text-[#C4A661] text-sm font-medium group-hover:gap-2 transition-all">
            Visit
            <ArrowSquareOut size={14} />
          </span>
        </div>
      </div>
    </motion.a>
  );
};

export default LinksResourcesPage;
